<template>
  <div class="container">
    <div class="card">
      <h1 class="unit-title special-style">CCNA Labs Beta Testing Rules</h1>

      <p>Welcome to the NetworkChuck Academy (NCA) CCNA Labs Beta Testing Program! As a beta tester, you're helping shape a purpose-driven, project-based learning experience that empowers learners to excel in their careers. Your feedback is critical to ensuring our CCNA labs are engaging, accurate, and relevant to real-world networking challenges. Below are the rules to guide your participation:</p>

      <h2>Access the Labs</h2>
      <p>No additional hardware, software, or specific internet speed is required. Once you are selected to be part of the Beta Test Program, just go to <a href="https://academy.networkchuck.com/" target="_blank" class="link">academy.networkchuck.com</a> and sign in. You will see the labs within the Cisco CCNA course. The first lab is in Skill 05, Lesson 02 (encoded as S05-L02); a complete list of lab locations and encodings is below in this document.</p>

      <h2>Focus of Feedback</h2>
      <ul>
        <li><strong>Clarity of Instructions:</strong> Are the lab instructions clear, concise, and easy to follow?</li>
        <li><strong>Accuracy:</strong> Do the lab simulations and exercises function correctly and align with CCNA concepts?</li>
        <li><strong>Real-World Relevance:</strong> Does the lab reflect tasks or skills a networking professional would encounter in a real-world job? Provide specific examples or suggestions if it feels disconnected from practical applications.</li>
      </ul>

      <h2>Feedback Submission</h2>
      <ul>
        <li>Submit feedback weekly for a minimum of TWO LABS, or more frequently if you encounter significant issues or have valuable insights.</li>
        <li>To submit feedback that may require more detail than the form permits (screenshots etc.), use <a href="mailto:CCNALabs@networkchuck.com" class="link">CCNALabs@networkchuck.com</a> for lab-related feedback. Include the video and lab title in the subject line. One email per lab please.</li>
        <li>For issues related to the CCNA course platform or content outside the labs, email <a href="mailto:hello@networkchuck.com" class="link">hello@networkchuck.com</a>.</li>
      </ul>

      <h2>Commitment and Timeline</h2>
      <p>The beta testing program runs until October 31, 2025. Review and provide feedback on at least two CCNA labs per week. Failure to meet this minimum requirement may result in removal from the program.</p>

      <h2>Incentives</h2>
      <p>As a thank you for your participation, you'll receive 90 days of free access to the full CCNA 2025 course. To remain eligible, you must consistently review at least two labs per week through October 31, 2025.</p>

      <h2>Where to Find the Labs</h2>
      <p>This is a list of the labs we currently have for review; there are more being added regularly, so please check back early and often:</p>

      <!-- Rest of your lab list content here - keeping it the same -->
      <div class="lab-list">
        <h3>Skill 05</h3>
      <ul>
        <li><strong>Skill 05</strong>
          <ul>
            <li>Lesson 02 (S05-L02)</li>
            <li>Lesson 04 (S05-L04)</li>
            <li>Lesson 05 (S05-L05)</li>
            <li>Skill Lab – found in last lesson of skill (S05-SL01)</li>
          </ul>
        </li>
        <li><strong>Skill 06</strong>
          <ul>
            <li>Lesson 02 (S06-L02)</li>
            <li>Lesson 04 (S06-L04)</li>
            <li>Lesson 05 (S06-L05)</li>
            <li>Skill Lab – found in last lesson of skill</li>
          </ul>
        </li>
        <li><strong>Skill 08</strong>
          <ul>
            <li>Lesson 01 (S08-L01)</li>
            <li>Lesson 02 (S08-L02)</li>
            <li>Lesson 03 (S08-L03)</li>
            <li>Skill Lab – found in last lesson of skill (S08-L01)</li>
          </ul>
        </li>
        <li><strong>Skill 09</strong>
          <ul>
            <li>Lesson 02 (S09-L02)</li>
            <li>Lesson 03 (S09-L03)</li>
            <li>Lesson 04 (S09-L04)</li>
            <li>Lesson 05 (S09-L05)</li>
            <li>Lesson 06 (S09-L06)</li>
            <li>Skill Lab – found in last lesson of skill (S09-SL01) – NOT YET AVAILABLE</li>
          </ul>
        </li>
        <li><strong>Skill 10</strong>
          <ul>
            <li>Lesson 02 (S10-L02)</li>
            <li>Lesson 04 (S10-L04)</li>
            <li>Lesson 05 (S10-L05)</li>
            <li>Lesson 06 (S10-L06)</li>
            <li>Skill Lab – found in last lesson of skill (S10-SL01) – NOT YET AVAILABLE</li>
          </ul>
        </li>
        <li><strong>Skill 11</strong>
          <ul>
            <li>Lesson 08.5 (S11-L08.5)</li>
            <li>Skill Lab – found in last lesson of skill (S11-L01) – NOT YET AVAILABLE</li>
          </ul>
        </li>
        <li><strong>Skill 12</strong>
          <ul>
            <li>Lesson 02 (S12-L02)</li>
            <li>Lesson 03 (S12-L03)</li>
            <li>Lesson 04 (S12-L04)</li>
            <li>Lesson 05 (S12-L05)</li>
            <li>Lesson 06 (S12-L06)</li>
            <li>Lesson 08 (S12-L08)</li>
            <li>Lesson 09 (S12-L09)</li>
            <li>Skill Lab – found in last lesson of skill (S12-SL01) – NOT YET AVAILABLE</li>
          </ul>
        </li>
        <li><strong>Skill 13</strong>
          <ul>
            <li>Lesson 04 (S13-L04)</li>
            <li>Lesson 05 (S13-L05)</li>
            <li>Lesson 06 (S13-L06)</li>
            <li>Lesson 07 (S13-L07)</li>
            <li>Skill Lab – found in last lesson of skill (S13-SL01) – NOT YET AVAILABLE</li>
          </ul>
        </li>
        <li><strong>Skill 14</strong>
          <ul>
            <li>Lesson 02 (S14-L02)</li>
            <li>Lesson 03 (S14-L03)</li>
            <li>Lesson 04 (S14-L04)</li>
            <li>Skill Lab – found in last lesson of skill (S14-SL01) – NOT YET AVAILABLE</li>
          </ul>
        </li>
        <li><strong>Skill 15</strong>
          <ul>
            <li>Lesson 04 (S15-L04)</li>
            <li>Lesson 05 (S15-L05)</li>
            <li>Lesson 06 (S15-L06)</li>
            <li>Lesson 07 (S15-L07)</li>
            <li>Skill Lab – found in last lesson of skill (S15-SL01) – NOT YET AVAILABLE</li>
          </ul>
        </li>
        <li><strong>Skill 16</strong>
          <ul>
            <li>Lesson 03 (S16-L03)</li>
            <li>Skill Lab – found in last lesson of skill (S16-SL01) – NOT YET AVAILABLE</li>
          </ul>
        </li>
        <li><strong>Skill 17</strong>
          <ul>
            <li>Lesson 03 (S17-L03)</li>
            <li>Lesson 04 (S17-L04)</li>
            <li>Skill Lab – found in last lesson of skill (S17-SL01) – NOT YET AVAILABLE</li>
          </ul>
        </li>
        <li><strong>Skill 18</strong>
          <ul>
            <li>Lesson 02 (S18-L02)</li>
            <li>Lesson 03 (S18-L03)</li>
            <li>Lesson 04 (S18-L04)</li>
            <li>Skill Lab – found in last lesson of skill (S18-SL01) – NOT YET AVAILABLE</li>
          </ul>
        </li>
        <li><strong>Skill 20</strong>
          <ul>
            <li>Lesson 01 (S20-L01)</li>
            <li>Lesson 02 (S20-L02)</li>
            <li>Lesson 03 (S20-L03)</li>
            <li>Lesson 04 (S20-L04)</li>
            <li>Skill Lab – found in last lesson of skill (S20-SL01) – NOT YET AVAILABLE</li>
          </ul>
        </li>
        <li><strong>Skill 21</strong>
          <ul>
            <li>Lesson 01 (S21-L01)</li>
            <li>Lesson 02 (S21-L02)</li>
            <li>Skill Lab – found in last lesson of skill (S21-SL01) – NOT YET AVAILABLE</li>
          </ul>
        </li>
        <li><strong>Skill 22</strong>
          <ul>
            <li>Lesson 01 (S22-L01)</li>
            <li>Lesson 02 (S22-L02)</li>
            <li>Lesson 03 (S22-L03)</li>
            <li>Lesson 04 (S22-L04)</li>
            <li>Lesson 04.5 (S22-L04.5)</li>
            <li>Lesson 05 (S22-L05)</li>
            <li>Skill Lab – found in last lesson of skill (S22-SL01)</li>
          </ul>
        </li>
        <li><strong>Skill 23</strong>
          <ul>
            <li>Lesson 01 (S23-L01)</li>
            <li>Lesson 03 (S23-L03)</li>
            <li>Skill Lab – found in last lesson of skill (S23-SL01)</li>
          </ul>
        </li>
        <li><strong>Skill 24</strong>
          <ul>
            <li>Lesson 02 (S24-L02)</li>
            <li>Lesson 03 (S24-L03)</li>
            <li>Skill Lab – found in last lesson of skill (S24-SL01)</li>
          </ul>
        </li>
      </ul>
        <!-- Add other skills as needed, but shortened for this example -->
      </div>

      <div class="support-section">
        <h2>Support and Communication</h2>
        <p>For lab-specific issues or suggestions, email <a href="mailto:CCNALabs@networkchuck.com" class="link">CCNALabs@networkchuck.com</a>.<br>
           For course platform issues (e.g., academy.networkchuck.com access problems), email <a href="mailto:hello@networkchuck.com" class="link">hello@networkchuck.com</a>.<br>
           Thank you for joining the NCA CCNA Labs Beta Testing Program! Your contributions will help create an engaging, project-based learning experience that empowers apprentices to achieve their career goals.</p>
      </div>

      <router-link to="/" class="back-button">← Back to Home</router-link>
    </div>
  </div>
</template>

<script>
export default {
  name: "ReviewerGuidelines",
  mounted() {
    console.log("ReviewerGuidelines mounted successfully!");
  }
};
</script>

